<template>
  <s-page class="max-w-7xl" v-if="isDataLoaded">
    <s-header class="px-6">Task Audit</s-header>

    <div class="px-6 py-4 space-y-6">
      <!-- Date Range Controls Card -->
      <div class="bg-white rounded-lg shadow-sm border border-gray-200">
        <div class="bg-gradient-to-r from-blue-50 to-indigo-50 p-6 border-b border-blue-100">
          <div class="flex items-center justify-between">
            <div class="flex items-center gap-4">
              <div
                class="w-12 h-12 bg-white rounded-xl shadow-sm border border-blue-100 flex items-center justify-center"
              >
              <svg
                class="w-6 h-6 text-blue-600"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                ></path>
              </svg>
            </div>
            <div>
              <h3 class="text-xl font-bold text-gray-900">
                Date Range Selection
              </h3>
              <p class="text-sm text-gray-600 mt-1">
                Choose a time period to analyze task completion data
              </p>
            </div>
          </div>
          
          <div class="flex items-center gap-3 mt-4">
            <button
              @click="refreshData"
              :disabled="loading"
              class="inline-flex items-center px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 text-white font-medium rounded-lg shadow-sm transition-colors focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
            >
              <svg
                v-if="loading"
                class="animate-spin -ml-1 mr-2 h-4 w-4 text-white"
                fill="none"
                viewBox="0 0 24 24"
              >
                <circle
                  class="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  stroke-width="4"
                ></circle>
                <path
                  class="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                ></path>
              </svg>
              <svg
                v-else
                class="w-4 h-4 mr-2"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                ></path>
              </svg>
              {{ loading ? 'Loading...' : 'Refresh Data' }}
            </button>
            
            <div
              v-if="dataValidationErrors.length > 0"
              class="flex items-center px-3 py-2 bg-amber-50 border border-amber-200 rounded-lg"
            >
              <svg
                class="w-4 h-4 mr-2 text-amber-600"
                fill="currentColor"
                viewBox="0 0 20 20"
              >
                <path
                  fill-rule="evenodd"
                  d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                  clip-rule="evenodd"
                ></path>
              </svg>
              <span class="text-sm font-medium text-amber-800"
                >{{ dataValidationErrors.length }} validation warning(s)</span
              >
            </div>
          </div>
          </div>
        </div>

        <div class="p-6">
          <div class="grid grid-cols-1 lg:grid-cols-3 gap-4">
          <!-- Preset Selection -->
          <div>
            <label
              for="date-preset"
              class="block text-sm font-medium text-gray-700 mb-2"
              >Quick Range</label
            >
            <div class="relative">
              <select
                id="date-preset"
                v-model="selectedPreset"
                @change="onPresetChange"
                class="w-full px-4 py-2.5 pr-10 border border-gray-300 rounded-lg shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all appearance-none bg-white text-gray-900"
              >
                <option value="today">Today</option>
                <option value="yesterday">Yesterday</option>
                <option value="last7">Last 7 days</option>
                <option value="last14">Last 14 days</option>
                <option value="last30">Last 30 days</option>
                <option value="custom">Custom Range</option>
              </select>
              <div class="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                <svg
                  class="w-5 h-5 text-gray-400"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M19 9l-7 7-7-7"
                  ></path>
                </svg>
              </div>
            </div>
          </div>

          <!-- Start Date -->
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-2"
              >Start Date</label
            >
            <v-date-picker
              v-model="startDate"
              @input="onDateChange"
              :max-date="endDate"
              :popover="{
                placement: 'bottom-start',
                positionFixed: true,
                class: 'z-50',
                visibility: 'click'
              }"
            >
              <template #default="{ inputValue, inputEvents }">
                <div class="relative">
                  <input
                    :value="inputValue"
                    v-on="inputEvents"
                    class="w-full h-[42px] pl-10 pr-4 border border-gray-300 rounded-lg shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all cursor-pointer bg-white text-gray-900"
                    placeholder="Select start date"
                    readonly
                  />
                  <div class="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                    <svg
                      class="w-5 h-5 text-gray-400"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        stroke-width="2"
                        d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                      ></path>
                    </svg>
                  </div>
                </div>
              </template>
            </v-date-picker>
          </div>
          
          <!-- End Date -->
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-2"
              >End Date</label
            >
            <v-date-picker
              v-model="endDate"
              @input="onDateChange"
              :min-date="startDate"
              :popover="{
                placement: 'bottom-start',
                positionFixed: true,
                class: 'z-50',
                visibility: 'click'
              }"
            >
              <template #default="{ inputValue, inputEvents }">
                <div class="relative">
                  <input
                    :value="inputValue"
                    v-on="inputEvents"
                    class="w-full h-[42px] pl-10 pr-4 border border-gray-300 rounded-lg shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all cursor-pointer bg-white text-gray-900"
                    placeholder="Select end date"
                    readonly
                  />
                  <div class="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                    <svg
                      class="w-5 h-5 text-gray-400"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        stroke-width="2"
                        d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                      ></path>
                    </svg>
                  </div>
                </div>
              </template>
            </v-date-picker>
          </div>
          </div>
        </div>
      </div>

      <!-- Action Controls Card -->
      <div class="bg-white rounded-lg shadow-sm border border-gray-200">
        <div class="p-6">
          <div class="flex flex-wrap items-center justify-between gap-4">
          <div class="flex flex-wrap items-center gap-3">
            <button
              @click="exportCsv"
              :disabled="!hasData"
              class="inline-flex items-center px-4 py-2 bg-green-600 hover:bg-green-700 disabled:bg-gray-400 text-white font-medium rounded-lg shadow-sm transition-colors focus:ring-2 focus:ring-green-500 focus:ring-offset-2"
            >
              <svg
                class="w-4 h-4 mr-2"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                ></path>
              </svg>
              Export CSV
            </button>

            <button
              v-if="isAdmin"
              @click="inspectTaskData"
              class="inline-flex items-center px-3 py-2 bg-gray-600 hover:bg-gray-700 text-white text-sm font-medium rounded-lg shadow-sm transition-colors focus:ring-2 focus:ring-gray-500 focus:ring-offset-2"
            >
              <svg
                class="w-4 h-4 mr-2"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                ></path>
              </svg>
              Debug Data
            </button>

            <button
              v-if="isAdmin && viewMode === 'analytics'"
              @click="debugAnalyticsData"
              class="inline-flex items-center px-3 py-2 bg-purple-600 hover:bg-purple-700 text-white text-sm font-medium rounded-lg shadow-sm transition-colors focus:ring-2 focus:ring-purple-500 focus:ring-offset-2"
            >
              <svg
                class="w-4 h-4 mr-2"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
                ></path>
              </svg>
              Debug Analytics
            </button>
          </div>

          <!-- View Mode Selector -->
          <div class="flex items-center gap-2">
            <span class="text-sm font-medium text-gray-700">View Mode:</span>
            <div class="flex bg-gray-50 rounded-lg p-1 border border-gray-200">
              <button
                v-for="mode in viewModes"
                :key="mode.value"
                @click="viewMode = mode.value"
                :class="[
                  'px-3 py-1.5 text-sm font-medium rounded-md transition-colors',
                  viewMode === mode.value
                    ? 'bg-white text-blue-600 shadow-sm border border-gray-200'
                    : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                ]"
              >
                {{ mode.label }}
              </button>
            </div>
          </div>
          </div>
        </div>
      </div>

      <!-- Summary View -->
      <div v-if="viewMode === 'summary'" class="space-y-6">
        <!-- Summary Stats Cards -->
        <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <div class="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <div class="flex items-center">
              <div class="flex-shrink-0">
                <div
                  class="w-10 h-10 bg-blue-50 rounded-lg flex items-center justify-center"
                >
                  <svg
                    class="w-5 h-5 text-blue-600"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"
                    ></path>
                  </svg>
                </div>
              </div>
              <div class="ml-4">
                <p class="text-sm font-medium text-gray-600">Total Logs</p>
                <p class="text-2xl font-semibold text-gray-900">
                  {{ summaryStats.totalLogs }}
                </p>
              </div>
            </div>
          </div>

          <div class="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <div class="flex items-center">
              <div class="flex-shrink-0">
                <div
                  class="w-10 h-10 bg-green-50 rounded-lg flex items-center justify-center"
                >
                  <svg
                    class="w-5 h-5 text-green-600"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                    ></path>
                  </svg>
                </div>
              </div>
              <div class="ml-4">
                <p class="text-sm font-medium text-gray-600">Completed</p>
                <p class="text-2xl font-semibold text-gray-900">
                  {{ summaryStats.completedLogs }}
                </p>
              </div>
            </div>
          </div>

          <div class="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <div class="flex items-center">
              <div class="flex-shrink-0">
                <div
                  class="w-10 h-10 bg-amber-50 rounded-lg flex items-center justify-center"
                >
                  <svg
                    class="w-5 h-5 text-amber-600"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                    ></path>
                  </svg>
                </div>
              </div>
              <div class="ml-4">
                <p class="text-sm font-medium text-gray-600">In Progress</p>
                <p class="text-2xl font-semibold text-gray-900">
                  {{ summaryStats.openLogs }}
                </p>
              </div>
            </div>
          </div>

          <div class="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <div class="flex items-center">
              <div class="flex-shrink-0">
                <div
                  class="w-10 h-10 bg-purple-50 rounded-lg flex items-center justify-center"
                >
                  <svg
                    class="w-5 h-5 text-purple-600"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M7 12l3-3 3 3 4-4M8 21l4-4 4 4M3 4h18M4 4h16v12a1 1 0 01-1 1H5a1 1 0 01-1-1V4z"
                    ></path>
                  </svg>
                </div>
              </div>
              <div class="ml-4">
                <p class="text-sm font-medium text-gray-600">Completion Rate</p>
                <p class="text-2xl font-semibold text-gray-900">
                  {{ summaryStats.completionRate }}%
                </p>
              </div>
            </div>
          </div>
        </div>

        <!-- Task Logs Table -->
        <div class="bg-white rounded-lg shadow-sm border border-gray-200">
          <div class="px-6 py-4 border-b border-gray-100">
            <div class="flex items-center justify-between">
              <h3 class="text-lg font-semibold text-gray-900">
                Task Logs ({{ formatRefreshedDateRange() }})
              </h3>
              <div class="text-sm text-gray-500">
                {{ taskLogs.length }} log{{ taskLogs.length !== 1 ? 's' : '' }}
                found
              </div>
            </div>
          </div>

          <div v-if="loading" class="flex items-center justify-center py-12">
            <div class="flex items-center space-x-3">
              <svg
                class="animate-spin h-5 w-5 text-blue-600"
                fill="none"
                viewBox="0 0 24 24"
              >
                <circle
                  class="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  stroke-width="4"
                ></circle>
                <path
                  class="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                ></path>
              </svg>
              <span class="text-gray-600">Loading task data...</span>
            </div>
          </div>

          <div
            v-else-if="taskLogs.length === 0"
            class="flex flex-col items-center justify-center py-12"
          >
            <svg
              class="w-12 h-12 text-gray-400 mb-4"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4"
              ></path>
            </svg>
            <h3 class="text-lg font-medium text-gray-900 mb-2">
              No task logs found
            </h3>
            <p class="text-gray-500 text-center max-w-sm">
              No task logs were found for the selected date range. Try adjusting
              your date selection or refresh the data.
            </p>
          </div>

          <div v-else class="overflow-x-auto">
            <table class="w-full">
              <thead class="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th
                    @click="sortSummaryBy('storeName')"
                    class="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wide cursor-pointer hover:bg-gray-100 transition-colors select-none"
                  >
                    <div class="flex items-center space-x-1">
                      <span>Store</span>
                      <div class="flex flex-col">
                        <svg
                          class="w-3 h-3"
                          :class="getSummarySortIconClass('storeName', 'asc')"
                          fill="currentColor"
                          viewBox="0 0 20 20"
                        >
                          <path
                            fill-rule="evenodd"
                            d="M14.707 12.707a1 1 0 01-1.414 0L10 9.414l-3.293 3.293a1 1 0 01-1.414-1.414l4-4a1 1 0 011.414 0l4 4a1 1 0 010 1.414z"
                            clip-rule="evenodd"
                          ></path>
                        </svg>
                        <svg
                          class="w-3 h-3 -mt-1"
                          :class="getSummarySortIconClass('storeName', 'desc')"
                          fill="currentColor"
                          viewBox="0 0 20 20"
                        >
                          <path
                            fill-rule="evenodd"
                            d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                            clip-rule="evenodd"
                          ></path>
                        </svg>
                      </div>
                    </div>
                  </th>
                  <th
                    @click="sortSummaryBy('type')"
                    class="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wide cursor-pointer hover:bg-gray-100 transition-colors select-none"
                  >
                    <div class="flex items-center space-x-1">
                      <span>Type</span>
                      <div class="flex flex-col">
                        <svg
                          class="w-3 h-3"
                          :class="getSummarySortIconClass('type', 'asc')"
                          fill="currentColor"
                          viewBox="0 0 20 20"
                        >
                          <path
                            fill-rule="evenodd"
                            d="M14.707 12.707a1 1 0 01-1.414 0L10 9.414l-3.293 3.293a1 1 0 01-1.414-1.414l4-4a1 1 0 011.414 0l4 4a1 1 0 010 1.414z"
                            clip-rule="evenodd"
                          ></path>
                        </svg>
                        <svg
                          class="w-3 h-3 -mt-1"
                          :class="getSummarySortIconClass('type', 'desc')"
                          fill="currentColor"
                          viewBox="0 0 20 20"
                        >
                          <path
                            fill-rule="evenodd"
                            d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                            clip-rule="evenodd"
                          ></path>
                        </svg>
                      </div>
                    </div>
                  </th>
                  <th
                    @click="sortSummaryBy('date')"
                    class="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wide cursor-pointer hover:bg-gray-100 transition-colors select-none"
                  >
                    <div class="flex items-center space-x-1">
                      <span>Date</span>
                      <div class="flex flex-col">
                        <svg
                          class="w-3 h-3"
                          :class="getSummarySortIconClass('date', 'asc')"
                          fill="currentColor"
                          viewBox="0 0 20 20"
                        >
                          <path
                            fill-rule="evenodd"
                            d="M14.707 12.707a1 1 0 01-1.414 0L10 9.414l-3.293 3.293a1 1 0 01-1.414-1.414l4-4a1 1 0 011.414 0l4 4a1 1 0 010 1.414z"
                            clip-rule="evenodd"
                          ></path>
                        </svg>
                        <svg
                          class="w-3 h-3 -mt-1"
                          :class="getSummarySortIconClass('date', 'desc')"
                          fill="currentColor"
                          viewBox="0 0 20 20"
                        >
                          <path
                            fill-rule="evenodd"
                            d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                            clip-rule="evenodd"
                          ></path>
                        </svg>
                      </div>
                    </div>
                  </th>
                  <th
                    @click="sortSummaryBy('completed')"
                    class="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wide cursor-pointer hover:bg-gray-100 transition-colors select-none"
                  >
                    <div class="flex items-center space-x-1">
                      <span>Status</span>
                      <div class="flex flex-col">
                        <svg
                          class="w-3 h-3"
                          :class="getSummarySortIconClass('completed', 'asc')"
                          fill="currentColor"
                          viewBox="0 0 20 20"
                        >
                          <path
                            fill-rule="evenodd"
                            d="M14.707 12.707a1 1 0 01-1.414 0L10 9.414l-3.293 3.293a1 1 0 01-1.414-1.414l4-4a1 1 0 011.414 0l4 4a1 1 0 010 1.414z"
                            clip-rule="evenodd"
                          ></path>
                        </svg>
                        <svg
                          class="w-3 h-3 -mt-1"
                          :class="getSummarySortIconClass('completed', 'desc')"
                          fill="currentColor"
                          viewBox="0 0 20 20"
                        >
                          <path
                            fill-rule="evenodd"
                            d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                            clip-rule="evenodd"
                          ></path>
                        </svg>
                      </div>
                    </div>
                  </th>
                  <th
                    @click="sortSummaryBy('completionPercentage')"
                    class="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wide cursor-pointer hover:bg-gray-100 transition-colors select-none"
                  >
                    <div class="flex items-center space-x-1">
                      <span>Progress</span>
                      <div class="flex flex-col">
                        <svg
                          class="w-3 h-3"
                          :class="
                            getSummarySortIconClass(
                              'completionPercentage',
                              'asc'
                            )
                          "
                          fill="currentColor"
                          viewBox="0 0 20 20"
                        >
                          <path
                            fill-rule="evenodd"
                            d="M14.707 12.707a1 1 0 01-1.414 0L10 9.414l-3.293 3.293a1 1 0 01-1.414-1.414l4-4a1 1 0 011.414 0l4 4a1 1 0 010 1.414z"
                            clip-rule="evenodd"
                          ></path>
                        </svg>
                        <svg
                          class="w-3 h-3 -mt-1"
                          :class="
                            getSummarySortIconClass(
                              'completionPercentage',
                              'desc'
                            )
                          "
                          fill="currentColor"
                          viewBox="0 0 20 20"
                        >
                          <path
                            fill-rule="evenodd"
                            d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                            clip-rule="evenodd"
                          ></path>
                        </svg>
                      </div>
                    </div>
                  </th>
                  <th
                    class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                  >
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody class="bg-white divide-y divide-gray-100">
                <tr
                  v-for="log in sortedTaskLogs"
                  :key="log.id"
                  class="hover:bg-gray-50 transition-colors"
                >
                  <td class="px-6 py-4 whitespace-nowrap">
                    <div class="text-sm font-medium text-gray-900">
                      {{ log.storeName }}
                    </div>
                  </td>
                  <td class="px-6 py-4 whitespace-nowrap">
                    <span
                      class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium"
                      :class="getTypeTagClass(log.type || 'daily')"
                    >
                      {{ log.type || 'daily' }}
                    </span>
                  </td>
                  <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {{ formatDate(log.date) }}
                  </td>
                  <td class="px-6 py-4 whitespace-nowrap">
                    <span :class="getModernStatusClass(log.completed)">
                      {{ log.completed ? 'Completed' : 'In Progress' }}
                    </span>
                  </td>
                  <td class="px-6 py-4 whitespace-nowrap">
                    <div class="flex items-center">
                      <div class="flex-1 mr-4">
                        <div
                          class="flex items-center justify-between text-sm mb-1"
                        >
                          <span class="font-medium text-gray-900"
                            >{{ displayCompletionPercentage(log) }}%</span
                          >
                          <span
                            class="text-gray-500"
                            v-if="log.totalTasksCount"
                          >
                            {{ log.completedTasksCount }}/{{
                              log.totalTasksCount
                            }}
                          </span>
                        </div>
                        <div class="w-full bg-gray-200 rounded-full h-2">
                          <div
                            class="h-2 rounded-full transition-all duration-300"
                            :class="
                              getProgressBarClass(
                                displayCompletionPercentage(log)
                              )
                            "
                            :style="{
                              width: `${displayCompletionPercentage(log)}%`
                            }"
                          ></div>
                        </div>
                      </div>
                    </div>
                  </td>
                  <td class="px-6 py-4 whitespace-nowrap text-sm font-medium">
                    <button
                      @click="viewTaskDetails(log)"
                      class="inline-flex items-center px-3 py-1.5 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors"
                    >
                      View Details
                    </button>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <!-- Detailed View -->
      <div v-else-if="viewMode === 'detailed'" class="space-y-6">
        <!-- Filters Card -->
        <div class="bg-white rounded-lg shadow-sm border border-gray-200">
          <div class="px-6 py-4 border-b border-gray-100">
            <div class="flex items-center justify-between">
              <h3 class="text-lg font-semibold text-gray-900">Filters</h3>
              <button
                @click="clearFilters"
                class="text-sm text-blue-600 hover:text-blue-700 font-medium transition-colors"
              >
                Clear All
              </button>
            </div>
          </div>
          <div class="p-6">
            <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              <div>
                <label class="block text-sm font-medium text-gray-700 mb-2"
                  >Store</label
                >
                <select
                  v-model="filters.store"
                  class="w-full h-[42px] px-3 border border-gray-300 rounded-lg shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all bg-white text-gray-900"
                >
                  <option value="">All Stores</option>
                <option
                  v-for="store in storeOptions"
                  :key="store"
                  :value="store"
                >
                  {{ store }}
                </option>
                </select>
              </div>

              <div>
                <label class="block text-sm font-medium text-gray-700 mb-2"
                  >Type</label
                >
                <select
                  v-model="filters.type"
                  class="w-full h-[42px] px-3 border border-gray-300 rounded-lg shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all bg-white text-gray-900"
                >
                  <option value="">All Types</option>
                <option value="daily">Daily</option>
                <option value="weekly">Weekly</option>
                <option value="monthly">Monthly</option>
                </select>
              </div>

              <div>
                <label class="block text-sm font-medium text-gray-700 mb-2"
                  >Status</label
                >
                <select
                  v-model="filters.status"
                  class="w-full h-[42px] px-3 border border-gray-300 rounded-lg shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all bg-white text-gray-900"
                >
                  <option value="">All Statuses</option>
                <option value="completed">Completed</option>
                <option value="open">In Progress</option>
                </select>
              </div>

              <div>
                <label class="block text-sm font-medium text-gray-700 mb-2"
                  >Search</label
                >
                <div class="relative">
                  <input
                    v-model="filters.search"
                    placeholder="Search tasks..."
                    class="w-full h-[42px] pl-10 pr-3 border border-gray-300 rounded-lg shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all text-gray-900"
                  />
                  <div class="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                    <svg
                      class="h-5 w-5 text-gray-400"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        stroke-width="2"
                        d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                      ></path>
                    </svg>
                  </div>
                </div>
              </div>
            </div>

            <!-- Active Filters Display -->
            <div
              v-if="hasActiveFilters"
              class="mt-4 pt-4 border-t border-gray-100"
            >
            <div class="flex flex-wrap items-center gap-2">
              <span class="text-sm font-medium text-gray-700"
                >Active filters:</span
              >
              <span
                v-if="filters.store"
                class="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-blue-100 text-blue-800"
              >
                Store: {{ filters.store }}
                <button
                  @click="filters.store = ''"
                  class="ml-2 inline-flex items-center justify-center w-4 h-4 rounded-full text-blue-400 hover:bg-blue-200 hover:text-blue-600 transition-colors"
                >
                  <svg
                    class="w-2 h-2"
                    stroke="currentColor"
                    fill="none"
                    viewBox="0 0 8 8"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-width="1.5"
                      d="m1 1 6 6m0-6-6 6"
                    />
                  </svg>
                </button>
              </span>
              <span
                v-if="filters.type"
                class="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-green-100 text-green-800"
              >
                Type: {{ filters.type }}
                <button
                  @click="filters.type = ''"
                  class="ml-2 inline-flex items-center justify-center w-4 h-4 rounded-full text-green-400 hover:bg-green-200 hover:text-green-600 transition-colors"
                >
                  <svg
                    class="w-2 h-2"
                    stroke="currentColor"
                    fill="none"
                    viewBox="0 0 8 8"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-width="1.5"
                      d="m1 1 6 6m0-6-6 6"
                    />
                  </svg>
                </button>
              </span>
              <span
                v-if="filters.status"
                class="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-purple-100 text-purple-800"
              >
                Status: {{ filters.status }}
                <button
                  @click="filters.status = ''"
                  class="ml-2 inline-flex items-center justify-center w-4 h-4 rounded-full text-purple-400 hover:bg-purple-200 hover:text-purple-600 transition-colors"
                >
                  <svg
                    class="w-2 h-2"
                    stroke="currentColor"
                    fill="none"
                    viewBox="0 0 8 8"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-width="1.5"
                      d="m1 1 6 6m0-6-6 6"
                    />
                  </svg>
                </button>
              </span>
              <span
                v-if="filters.search"
                class="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-amber-100 text-amber-800"
              >
                Search: "{{ filters.search }}"
                <button
                  @click="filters.search = ''"
                  class="ml-2 inline-flex items-center justify-center w-4 h-4 rounded-full text-amber-400 hover:bg-amber-200 hover:text-amber-600 transition-colors"
                >
                  <svg
                    class="w-2 h-2"
                    stroke="currentColor"
                    fill="none"
                    viewBox="0 0 8 8"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-width="1.5"
                      d="m1 1 6 6m0-6-6 6"
                    />
                  </svg>
                </button>
              </span>
            </div>
            </div>
          </div>
        </div>

        <!-- Loading State -->
        <div
          v-if="loading"
          class="bg-white rounded-xl shadow-sm border border-gray-100 p-12"
        >
          <div class="flex flex-col items-center justify-center">
            <svg
              class="animate-spin h-8 w-8 text-blue-600 mb-4"
              fill="none"
              viewBox="0 0 24 24"
            >
              <circle
                class="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                stroke-width="4"
              ></circle>
              <path
                class="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
              ></path>
            </svg>
            <h3 class="text-lg font-medium text-gray-900 mb-2">
              Loading Task Data
            </h3>
            <p class="text-gray-500">Fetching detailed task information...</p>
          </div>
        </div>

        <!-- Empty State -->
        <div
          v-else-if="filteredDetailedTasks.length === 0"
          class="bg-white rounded-xl shadow-sm border border-gray-100 p-12"
        >
          <div class="flex flex-col items-center justify-center">
            <svg
              class="w-16 h-16 text-gray-400 mb-4"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4"
              ></path>
            </svg>
            <h3 class="text-lg font-medium text-gray-900 mb-2">
              No Tasks Found
            </h3>
            <p class="text-gray-500 text-center max-w-sm">
              No tasks were found matching your current filters. Try adjusting
              your search criteria or clearing the filters.
            </p>
          </div>
        </div>

        <!-- Tasks Table -->
        <div
          v-else
          class="bg-white rounded-lg shadow-sm border border-gray-200"
        >
          <div class="px-6 py-4 border-b border-gray-100">
            <div class="flex items-center justify-between">
              <h3 class="text-lg font-semibold text-gray-900">
                Task Details
                <span class="ml-2 text-sm font-normal text-gray-500">
                  ({{ filteredDetailedTasks.length.toLocaleString() }} tasks)
                </span>
              </h3>
              <div class="text-sm text-gray-500">
                Showing <span class="font-medium text-gray-700">{{ (paginationStart + 1).toLocaleString() }}-{{ Math.min(paginationEnd, filteredDetailedTasks.length).toLocaleString() }}</span>
                of <span class="font-medium text-gray-700">{{ filteredDetailedTasks.length.toLocaleString() }}</span>
              </div>
            </div>
          </div>

          <div class="overflow-x-auto">
            <table class="w-full">
              <thead class="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th
                    @click="sortBy('name')"
                    class="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wide cursor-pointer hover:bg-gray-100 transition-colors select-none"
                  >
                    <div class="flex items-center space-x-1">
                      <span>Task Name</span>
                      <div class="flex flex-col">
                        <svg
                          class="w-3 h-3"
                          :class="getSortIconClass('name', 'asc')"
                          fill="currentColor"
                          viewBox="0 0 20 20"
                        >
                          <path
                            fill-rule="evenodd"
                            d="M14.707 12.707a1 1 0 01-1.414 0L10 9.414l-3.293 3.293a1 1 0 01-1.414-1.414l4-4a1 1 0 011.414 0l4 4a1 1 0 010 1.414z"
                            clip-rule="evenodd"
                          ></path>
                        </svg>
                        <svg
                          class="w-3 h-3 -mt-1"
                          :class="getSortIconClass('name', 'desc')"
                          fill="currentColor"
                          viewBox="0 0 20 20"
                        >
                          <path
                            fill-rule="evenodd"
                            d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                            clip-rule="evenodd"
                          ></path>
                        </svg>
                      </div>
                    </div>
                  </th>
                  <th
                    @click="sortBy('storeName')"
                    class="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wide cursor-pointer hover:bg-gray-100 transition-colors select-none"
                  >
                    <div class="flex items-center space-x-1">
                      <span>Store</span>
                      <div class="flex flex-col">
                        <svg
                          class="w-3 h-3"
                          :class="getSortIconClass('storeName', 'asc')"
                          fill="currentColor"
                          viewBox="0 0 20 20"
                        >
                          <path
                            fill-rule="evenodd"
                            d="M14.707 12.707a1 1 0 01-1.414 0L10 9.414l-3.293 3.293a1 1 0 01-1.414-1.414l4-4a1 1 0 011.414 0l4 4a1 1 0 010 1.414z"
                            clip-rule="evenodd"
                          ></path>
                        </svg>
                        <svg
                          class="w-3 h-3 -mt-1"
                          :class="getSortIconClass('storeName', 'desc')"
                          fill="currentColor"
                          viewBox="0 0 20 20"
                        >
                          <path
                            fill-rule="evenodd"
                            d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                            clip-rule="evenodd"
                          ></path>
                        </svg>
                      </div>
                    </div>
                  </th>
                  <th
                    @click="sortBy('type')"
                    class="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wide cursor-pointer hover:bg-gray-100 transition-colors select-none"
                  >
                    <div class="flex items-center space-x-1">
                      <span>Type</span>
                      <div class="flex flex-col">
                        <svg
                          class="w-3 h-3"
                          :class="getSortIconClass('type', 'asc')"
                          fill="currentColor"
                          viewBox="0 0 20 20"
                        >
                          <path
                            fill-rule="evenodd"
                            d="M14.707 12.707a1 1 0 01-1.414 0L10 9.414l-3.293 3.293a1 1 0 01-1.414-1.414l4-4a1 1 0 011.414 0l4 4a1 1 0 010 1.414z"
                            clip-rule="evenodd"
                          ></path>
                        </svg>
                        <svg
                          class="w-3 h-3 -mt-1"
                          :class="getSortIconClass('type', 'desc')"
                          fill="currentColor"
                          viewBox="0 0 20 20"
                        >
                          <path
                            fill-rule="evenodd"
                            d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                            clip-rule="evenodd"
                          ></path>
                        </svg>
                      </div>
                    </div>
                  </th>
                  <th
                    @click="sortBy('date')"
                    class="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wide cursor-pointer hover:bg-gray-100 transition-colors select-none"
                  >
                    <div class="flex items-center space-x-1">
                      <span>Date</span>
                      <div class="flex flex-col">
                        <svg
                          class="w-3 h-3"
                          :class="getSortIconClass('date', 'asc')"
                          fill="currentColor"
                          viewBox="0 0 20 20"
                        >
                          <path
                            fill-rule="evenodd"
                            d="M14.707 12.707a1 1 0 01-1.414 0L10 9.414l-3.293 3.293a1 1 0 01-1.414-1.414l4-4a1 1 0 011.414 0l4 4a1 1 0 010 1.414z"
                            clip-rule="evenodd"
                          ></path>
                        </svg>
                        <svg
                          class="w-3 h-3 -mt-1"
                          :class="getSortIconClass('date', 'desc')"
                          fill="currentColor"
                          viewBox="0 0 20 20"
                        >
                          <path
                            fill-rule="evenodd"
                            d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                            clip-rule="evenodd"
                          ></path>
                        </svg>
                      </div>
                    </div>
                  </th>
                  <th
                    @click="sortBy('completed')"
                    class="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wide cursor-pointer hover:bg-gray-100 transition-colors select-none"
                  >
                    <div class="flex items-center space-x-1">
                      <span>Status</span>
                      <div class="flex flex-col">
                        <svg
                          class="w-3 h-3"
                          :class="getSortIconClass('completed', 'asc')"
                          fill="currentColor"
                          viewBox="0 0 20 20"
                        >
                          <path
                            fill-rule="evenodd"
                            d="M14.707 12.707a1 1 0 01-1.414 0L10 9.414l-3.293 3.293a1 1 0 01-1.414-1.414l4-4a1 1 0 011.414 0l4 4a1 1 0 010 1.414z"
                            clip-rule="evenodd"
                          ></path>
                        </svg>
                        <svg
                          class="w-3 h-3 -mt-1"
                          :class="getSortIconClass('completed', 'desc')"
                          fill="currentColor"
                          viewBox="0 0 20 20"
                        >
                          <path
                            fill-rule="evenodd"
                            d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                            clip-rule="evenodd"
                          ></path>
                        </svg>
                      </div>
                    </div>
                  </th>
                  <th
                    @click="sortBy('completedBy')"
                    class="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wide cursor-pointer hover:bg-gray-100 transition-colors select-none"
                  >
                    <div class="flex items-center space-x-1">
                      <span>Completed By</span>
                      <div class="flex flex-col">
                        <svg
                          class="w-3 h-3"
                          :class="getSortIconClass('completedBy', 'asc')"
                          fill="currentColor"
                          viewBox="0 0 20 20"
                        >
                          <path
                            fill-rule="evenodd"
                            d="M14.707 12.707a1 1 0 01-1.414 0L10 9.414l-3.293 3.293a1 1 0 01-1.414-1.414l4-4a1 1 0 011.414 0l4 4a1 1 0 010 1.414z"
                            clip-rule="evenodd"
                          ></path>
                        </svg>
                        <svg
                          class="w-3 h-3 -mt-1"
                          :class="getSortIconClass('completedBy', 'desc')"
                          fill="currentColor"
                          viewBox="0 0 20 20"
                        >
                          <path
                            fill-rule="evenodd"
                            d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                            clip-rule="evenodd"
                          ></path>
                        </svg>
                      </div>
                    </div>
                  </th>
                  <th
                    @click="sortBy('completedAt')"
                    class="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wide cursor-pointer hover:bg-gray-100 transition-colors select-none"
                  >
                    <div class="flex items-center space-x-1">
                      <span>Completed At</span>
                      <div class="flex flex-col">
                        <svg
                          class="w-3 h-3"
                          :class="getSortIconClass('completedAt', 'asc')"
                          fill="currentColor"
                          viewBox="0 0 20 20"
                        >
                          <path
                            fill-rule="evenodd"
                            d="M14.707 12.707a1 1 0 01-1.414 0L10 9.414l-3.293 3.293a1 1 0 01-1.414-1.414l4-4a1 1 0 011.414 0l4 4a1 1 0 010 1.414z"
                            clip-rule="evenodd"
                          ></path>
                        </svg>
                        <svg
                          class="w-3 h-3 -mt-1"
                          :class="getSortIconClass('completedAt', 'desc')"
                          fill="currentColor"
                          viewBox="0 0 20 20"
                        >
                          <path
                            fill-rule="evenodd"
                            d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                            clip-rule="evenodd"
                          ></path>
                        </svg>
                      </div>
                    </div>
                  </th>
                </tr>
              </thead>
              <tbody class="bg-white divide-y divide-gray-100">
                <tr
                  v-for="task in paginatedTasks"
                  :key="task.id"
                  class="hover:bg-gray-50 transition-colors"
                >
                  <td class="px-6 py-4 whitespace-nowrap">
                    <div class="text-sm font-medium text-gray-900">
                      {{ task.name }}
                    </div>
                  </td>
                  <td class="px-6 py-4 whitespace-nowrap">
                    <div class="text-sm text-gray-900">
                      {{ task.storeName }}
                    </div>
                  </td>
                  <td class="px-6 py-4 whitespace-nowrap">
                    <span
                      class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium"
                      :class="getTypeTagClass(task.type || 'daily')"
                    >
                      {{ task.type || 'daily' }}
                    </span>
                  </td>
                  <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {{ formatDate(task.date) }}
                  </td>
                  <td class="px-6 py-4 whitespace-nowrap">
                    <span :class="getModernStatusClass(task.completed)">
                      {{ task.completed ? 'Completed' : 'In Progress' }}
                    </span>
                  </td>
                  <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {{ task.completedBy || '—' }}
                  </td>
                  <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {{
                      task.completedAt ? formatDateTime(task.completedAt) : '—'
                    }}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        <!-- Pagination Controls -->
        <div
          v-if="filteredDetailedTasks.length > 0"
          class="bg-white rounded-lg shadow-sm border border-gray-200 px-6 py-4"
        >
          <div class="flex items-center justify-between">
            <div class="flex items-center gap-4">
              <div class="text-sm text-gray-600">
                Showing
                <span class="font-semibold text-gray-900">{{ (paginationStart + 1).toLocaleString() }}-{{
                  Math.min(paginationEnd, filteredDetailedTasks.length).toLocaleString()
                }}</span>
                of
                <span class="font-semibold text-gray-900">{{
                  filteredDetailedTasks.length.toLocaleString()
                }}</span>
                tasks
              </div>
              <div class="h-4 w-px bg-gray-300"></div>
              <div class="text-sm font-medium text-gray-700">
                Page {{ currentPage }} of {{ totalPages }}
              </div>
            </div>

            <div class="flex items-center gap-2">
              <button
                @click="currentPage = Math.max(1, currentPage - 1)"
                :disabled="currentPage === 1"
                :class="[
                  'inline-flex items-center px-3 py-2 text-sm font-medium rounded-md border transition-all',
                  currentPage === 1
                    ? 'border-gray-200 text-gray-400 bg-gray-50 cursor-not-allowed'
                    : 'border-gray-300 text-gray-700 bg-white hover:bg-gray-50 hover:border-gray-400 focus:ring-2 focus:ring-blue-500 focus:ring-offset-2'
                ]"
              >
                <svg
                  class="w-4 h-4 mr-1"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M15 19l-7-7 7-7"
                  ></path>
                </svg>
                Previous
              </button>

              <button
                @click="currentPage = Math.min(totalPages, currentPage + 1)"
                :disabled="currentPage === totalPages"
                :class="[
                  'inline-flex items-center px-3 py-2 text-sm font-medium rounded-md border transition-all',
                  currentPage === totalPages
                    ? 'border-gray-200 text-gray-400 bg-gray-50 cursor-not-allowed'
                    : 'border-gray-300 text-gray-700 bg-white hover:bg-gray-50 hover:border-gray-400 focus:ring-2 focus:ring-blue-500 focus:ring-offset-2'
                ]"
              >
                Next
                <svg
                  class="w-4 h-4 ml-1"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M9 5l7 7-7 7"
                  ></path>
                </svg>
              </button>
            </div>
          </div>
        </div>
      </div>

      <!-- Analytics View -->
      <div v-else-if="viewMode === 'analytics'" class="space-y-6">
        <!-- Analytics Header -->
        <div class="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
          <div class="flex items-center justify-between mb-4">
            <h2 class="text-xl font-bold text-gray-900">
              Task Completion Analytics ({{ formatRefreshedDateRange() }})
            </h2>
            <div
              v-if="analyticsStats.totalLogs > 0"
              class="text-sm text-gray-500"
            >
              {{ analyticsStats.totalLogs }} logs analyzed
            </div>
          </div>

          <!-- Task Type Filter -->
          <div class="flex items-center space-x-3">
            <label class="text-sm font-medium text-gray-700"
              >Filter by Task Type:</label
            >
            <div class="flex bg-gray-100 rounded-lg p-1">
              <button
                v-for="type in ['all', 'daily', 'weekly', 'monthly']"
                :key="type"
                @click="analyticsFilter.type = type"
                :class="[
                  'px-3 py-1.5 text-sm font-medium rounded-md transition-colors',
                  analyticsFilter.type === type
                    ? 'bg-white text-gray-900 shadow-sm'
                    : 'text-gray-600 hover:text-gray-900'
                ]"
              >
                {{ type === 'all' ? 'All Types' : capitalize(type) }}
              </button>
            </div>
          </div>
        </div>

        <!-- Loading State -->
        <div
          v-if="loading"
          class="bg-white rounded-xl shadow-sm border border-gray-100 p-12"
        >
          <div class="flex flex-col items-center justify-center">
            <svg
              class="animate-spin h-8 w-8 text-blue-600 mb-4"
              fill="none"
              viewBox="0 0 24 24"
            >
              <circle
                class="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                stroke-width="4"
              ></circle>
              <path
                class="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
              ></path>
            </svg>
            <h3 class="text-lg font-medium text-gray-900 mb-2">
              Loading Analytics
            </h3>
            <p class="text-gray-500">Analyzing task completion data...</p>
          </div>
        </div>

        <!-- Empty State -->
        <div
          v-else-if="!taskLogs.length"
          class="bg-white rounded-xl shadow-sm border border-gray-100 p-12"
        >
          <div class="flex flex-col items-center justify-center">
            <svg
              class="w-16 h-16 text-gray-400 mb-4"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
              ></path>
            </svg>
            <h3 class="text-lg font-medium text-gray-900 mb-2">
              No Analytics Data
            </h3>
            <p class="text-gray-500 text-center max-w-sm">
              No task logs were found for the selected date range. Try adjusting
              your date selection or refresh the data to see analytics.
            </p>
          </div>
        </div>

        <!-- Analytics Content -->
        <div v-else class="space-y-6">
          <!-- Completion Matrix -->
          <div class="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
            <div class="flex items-center justify-between mb-6">
              <h3 class="text-lg font-semibold text-gray-900">
                Task Completion Matrix
              </h3>
              <div class="flex items-center space-x-4 text-sm">
                <div class="flex items-center space-x-2">
                  <div class="w-3 h-3 bg-green-500 rounded-full"></div>
                  <span class="text-gray-600">Completed</span>
                </div>
                <div class="flex items-center space-x-2">
                  <div class="w-3 h-3 bg-amber-500 rounded-full"></div>
                  <span class="text-gray-600">Partial</span>
                </div>
                <div class="flex items-center space-x-2">
                  <div class="w-3 h-3 bg-red-500 rounded-full"></div>
                  <span class="text-gray-600">Incomplete</span>
                </div>
              </div>
            </div>

            <div class="overflow-x-auto">
              <table class="w-full">
                <thead>
                  <tr class="border-b border-gray-200">
                    <th class="text-left py-3 px-4 font-medium text-gray-900">
                      Store
                    </th>
                    <th
                      v-for="date in uniqueDates"
                      :key="date"
                      class="text-center py-3 px-2 font-medium text-gray-900 min-w-20"
                    >
                      <div class="text-xs">{{ formatDateString(date) }}</div>
                    </th>
                  </tr>
                </thead>
                <tbody class="divide-y divide-gray-100">
                  <tr
                    v-for="store in uniqueStores"
                    :key="store"
                    class="hover:bg-gray-50 transition-colors"
                  >
                    <td class="py-4 px-4 font-medium text-gray-900">
                      {{ store }}
                    </td>
                    <td
                      v-for="date in uniqueDates"
                      :key="`${store}-${date}`"
                      class="py-4 px-2 text-center"
                    >
                      <div
                        v-if="getCompletionData(date, store)"
                        class="flex flex-col items-center space-y-2"
                      >
                        <!-- Modern Completion Ring -->
                        <div 
                          class="relative w-10 h-10 cursor-pointer hover:scale-110 transition-transform"
                          @click="viewAnalyticsDetails(date, store)"
                          :title="`View details for ${store} on ${formatDateString(date)}`"
                        >
                          <svg
                            class="w-10 h-10 transform -rotate-90"
                            viewBox="0 0 36 36"
                          >
                            <!-- Background circle -->
                            <path
                              class="text-gray-200"
                              stroke="currentColor"
                              stroke-width="3"
                              fill="none"
                              d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
                            />
                            <!-- Progress circle -->
                            <path
                              :class="
                                getCompletionRingColor(
                                  getCompletionData(date, store).percentage
                                )
                              "
                              stroke="currentColor"
                              stroke-width="3"
                              stroke-linecap="round"
                              fill="none"
                              :stroke-dasharray="`${
                                getCompletionData(date, store).percentage
                              }, 100`"
                              d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
                            />
                          </svg>
                          <!-- Center icon -->
                          <div
                            class="absolute inset-0 flex items-center justify-center"
                          >
                            <svg
                              v-if="getCompletionData(date, store).completed"
                              class="w-4 h-4 text-green-600"
                              fill="currentColor"
                              viewBox="0 0 20 20"
                            >
                              <path
                                fill-rule="evenodd"
                                d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                                clip-rule="evenodd"
                              ></path>
                            </svg>
                            <svg
                              v-else-if="
                                getCompletionData(date, store).percentage > 0
                              "
                              class="w-4 h-4 text-amber-600"
                              fill="currentColor"
                              viewBox="0 0 20 20"
                            >
                              <path
                                fill-rule="evenodd"
                                d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z"
                                clip-rule="evenodd"
                              ></path>
                            </svg>
                            <svg
                              v-else
                              class="w-4 h-4 text-red-600"
                              fill="currentColor"
                              viewBox="0 0 20 20"
                            >
                              <path
                                fill-rule="evenodd"
                                d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                                clip-rule="evenodd"
                              ></path>
                            </svg>
                          </div>
                        </div>
                        <!-- Percentage -->
                        <div class="text-xs font-medium text-gray-700">
                          {{ getCompletionData(date, store).percentage }}%
                        </div>
                      </div>
                      <div v-else class="flex items-center justify-center h-10">
                        <span class="text-gray-400 text-sm">—</span>
                      </div>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          <!-- Charts Section -->
          <div class="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <!-- Store Completion Donut Chart -->
            <div
              class="bg-white rounded-xl shadow-sm border border-gray-100 p-6"
            >
              <div class="flex items-center justify-between mb-6">
                <h3 class="text-lg font-semibold text-gray-900">
                  Store Completion Rates
                </h3>
                <div class="text-sm text-gray-500">
                  {{ Object.keys(storeCompletionRates).length }} stores
                </div>
              </div>

              <div
                v-if="Object.keys(storeCompletionRates).length === 0"
                class="flex flex-col items-center justify-center h-64 text-gray-500"
              >
                <svg
                  class="w-12 h-12 mb-3"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
                  ></path>
                </svg>
                <p>No store data available</p>
              </div>

              <div v-else class="space-y-4">
                <div
                  v-for="(rate, store) in storeCompletionRates"
                  :key="store"
                  class="group hover:bg-gray-50 rounded-lg p-3 transition-colors cursor-pointer"
                  @click="filterByStore(store)"
                >
                  <div class="flex items-center justify-between mb-3">
                    <div class="flex items-center space-x-3">
                      <div
                        class="w-3 h-3 rounded-full"
                        :class="getStoreColorClass(store)"
                      ></div>
                      <span class="font-medium text-gray-900">{{ store }}</span>
                    </div>
                    <div class="text-right">
                      <div class="text-lg font-semibold text-gray-900">
                        {{ rate.percentage }}%
                      </div>
                      <div class="text-xs text-gray-500">
                        {{ rate.completed }}/{{ rate.total }}
                      </div>
                    </div>
                  </div>

                  <!-- Enhanced Progress Bar -->
                  <div class="relative">
                    <div class="w-full bg-gray-200 rounded-full h-3">
                      <div
                        class="h-3 rounded-full transition-all duration-500 ease-out"
                        :class="getProgressBarClass(rate.percentage)"
                        :style="{ width: `${rate.percentage}%` }"
                      ></div>
                    </div>
                    <!-- Gradient overlay for visual appeal -->
                    <div
                      class="absolute inset-0 bg-gradient-to-r from-transparent to-white opacity-20 rounded-full"
                    ></div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Task Type Completion Chart -->
            <div
              class="bg-white rounded-xl shadow-sm border border-gray-100 p-6"
            >
              <div class="flex items-center justify-between mb-6">
                <h3 class="text-lg font-semibold text-gray-900">
                  Task Type Completion Rates
                </h3>
                <div class="text-sm text-gray-500">
                  {{
                    Object.keys(typeCompletionRates).filter(
                      (type) => typeCompletionRates[type].total > 0
                    ).length
                  }}
                  types
                </div>
              </div>

              <div
                v-if="
                  Object.keys(typeCompletionRates).filter(
                    (type) => typeCompletionRates[type].total > 0
                  ).length === 0
                "
                class="flex flex-col items-center justify-center h-64 text-gray-500"
              >
                <svg
                  class="w-12 h-12 mb-3"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"
                  ></path>
                </svg>
                <p>No task type data available</p>
              </div>

              <div v-else class="space-y-4">
                <div
                  v-for="(rate, type) in typeCompletionRates"
                  :key="type"
                  v-show="rate.total > 0"
                  class="group hover:bg-gray-50 rounded-lg p-3 transition-colors cursor-pointer"
                  @click="filterByType(type)"
                >
                  <div class="flex items-center justify-between mb-3">
                    <div class="flex items-center space-x-3">
                      <div
                        class="w-3 h-3 rounded-full"
                        :class="getTypeColorClass(type)"
                      ></div>
                      <span class="font-medium text-gray-900 capitalize">{{
                        type
                      }}</span>
                    </div>
                    <div class="text-right">
                      <div class="text-lg font-semibold text-gray-900">
                        {{ rate.percentage }}%
                      </div>
                      <div class="text-xs text-gray-500">
                        {{ rate.completed }}/{{ rate.total }}
                      </div>
                    </div>
                  </div>

                  <!-- Enhanced Progress Bar -->
                  <div class="relative">
                    <div class="w-full bg-gray-200 rounded-full h-3">
                      <div
                        class="h-3 rounded-full transition-all duration-500 ease-out"
                        :class="getTypeProgressClass(type, rate.percentage)"
                        :style="{ width: `${rate.percentage}%` }"
                      ></div>
                    </div>
                    <!-- Gradient overlay for visual appeal -->
                    <div
                      class="absolute inset-0 bg-gradient-to-r from-transparent to-white opacity-20 rounded-full"
                    ></div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Completion Trend Chart -->
          <div class="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
            <div class="flex items-center justify-between mb-6">
              <h3 class="text-lg font-semibold text-gray-900">
                Completion Trend ({{ formatRefreshedDateRange() }})
              </h3>
              <div class="flex items-center space-x-4 text-sm">
                <div class="flex items-center space-x-2">
                  <div class="w-3 h-3 bg-blue-500 rounded-full"></div>
                  <span class="text-gray-600">Completion Rate</span>
                </div>
                <div class="text-gray-500">
                  {{ completionTrend.length }} data points
                </div>
              </div>
            </div>

            <div
              v-if="completionTrend.length === 0"
              class="flex flex-col items-center justify-center h-64 text-gray-500"
            >
              <svg
                class="w-12 h-12 mb-3"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
                ></path>
              </svg>
              <p>No trend data available</p>
            </div>

            <div v-else class="relative">
              <!-- Chart Container -->
              <div class="h-64 relative">
                <!-- Y-axis labels -->
                <div
                  class="absolute left-0 top-0 h-full flex flex-col justify-between text-xs text-gray-500 pr-2"
                >
                  <span>100%</span>
                  <span>75%</span>
                  <span>50%</span>
                  <span>25%</span>
                  <span>0%</span>
                </div>

                <!-- Chart area -->
                <div class="ml-8 h-full relative">
                  <!-- Grid lines -->
                  <div class="absolute inset-0">
                    <div class="h-full flex flex-col justify-between">
                      <div class="border-t border-gray-200"></div>
                      <div class="border-t border-gray-200"></div>
                      <div class="border-t border-gray-200"></div>
                      <div class="border-t border-gray-200"></div>
                      <div class="border-t border-gray-200"></div>
                    </div>
                  </div>

                  <!-- Area chart -->
                  <div class="absolute inset-0 flex items-end">
                    <div
                      v-for="(day, index) in completionTrend"
                      :key="index"
                      class="flex-1 relative group cursor-pointer"
                      @mouseenter="showTrendTooltip(day, $event)"
                      @mouseleave="hideTrendTooltip"
                    >
                      <!-- Bar -->
                      <div class="relative h-full flex flex-col justify-end">
                        <div
                          class="w-full transition-all duration-300 group-hover:opacity-80"
                          :class="getTrendBarClass(day.percentage)"
                          :style="{ height: `${Math.max(day.percentage, 2)}%` }"
                        ></div>
                      </div>

                      <!-- Data point -->
                      <div
                        class="absolute top-0 left-1/2 transform -translate-x-1/2 w-2 h-2 bg-blue-600 rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                        :style="{ top: `${100 - day.percentage}%` }"
                      ></div>
                    </div>
                  </div>
                </div>
              </div>

              <!-- X-axis labels -->
              <div class="ml-8 mt-2 flex">
                <div
                  v-for="(day, index) in completionTrend"
                  :key="index"
                  class="flex-1 text-center"
                >
                  <div
                    class="text-xs text-gray-500 transform -rotate-45 origin-center"
                  >
                    {{ day.date }}
                  </div>
                </div>
              </div>

              <!-- Tooltip -->
              <div
                v-if="trendTooltip.show"
                class="absolute z-10 bg-gray-900 text-white text-sm rounded-lg py-2 px-3 pointer-events-none"
                :style="{
                  left: trendTooltip.x + 'px',
                  top: trendTooltip.y + 'px'
                }"
              >
                <div class="font-medium">{{ trendTooltip.data.date }}</div>
                <div>Completion: {{ trendTooltip.data.percentage }}%</div>
                <div>
                  Tasks: {{ trendTooltip.data.completed }}/{{
                    trendTooltip.data.total
                  }}
                </div>
                <div
                  v-if="trendTooltip.data.logsCount"
                  class="text-xs opacity-75"
                >
                  {{ trendTooltip.data.logsCount }} log(s)
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Task Details Modal -->
    <modal
      name="task-details-modal"
      height="auto"
      :adaptive="true"
      :scrollable="true"
    >
      <div class="p-4">
        <h3 class="text-xl font-bold mb-4">
          {{ selectedLog ? selectedLog.storeName : '' }} -
          {{ selectedLog ? selectedLog.type || 'daily' : '' }} Tasks
        </h3>
        <div v-if="selectedLog" class="mb-4">
          <div
            v-for="task in selectedLog.tasks"
            :key="task.id"
            class="border-b py-2"
          >
            <div class="flex items-center">
              <div class="w-6 h-6 mr-2">
                <div
                  v-if="task.completed"
                  class="w-full h-full bg-green-500 rounded-full flex items-center justify-center text-white"
                >
                  ✓
                </div>
                <div
                  v-else
                  class="w-full h-full border-2 border-gray-300 rounded-full"
                ></div>
              </div>
              <div class="flex-1">
                <p class="font-medium">{{ task.name }}</p>
                <p v-if="task.completedAt" class="text-sm text-gray-600">
                  Completed: {{ formatDateTime(task.completedAt) }}
                </p>
                <p v-if="task.completedBy" class="text-sm text-gray-600">
                  By: {{ task.completedBy }}
                </p>
              </div>
            </div>
          </div>
        </div>
        <div class="flex justify-end">
          <s-button @click="$modal.hide('task-details-modal')" class="shadow">
            Close
          </s-button>
        </div>
      </div>
    </modal>
  </s-page>
</template>

<script>
import { mapGetters, mapState, mapActions } from 'vuex'
import SPage from '@/components/SPage'
import SHeader from '@/components/SHeader'
import SButton from '@/components/Button'
import { DateTime } from 'luxon'
import { downloadCsv } from '@/utils'

export default {
  name: 'TaskAudit',
  components: {
    SPage,
    SHeader,
    SButton
  },
  data() {
    return {
      selectedLog: null,
      selectedPreset: 'last14',
      startDate: null,
      endDate: null,
      lastRefreshedStartDate: null,
      lastRefreshedEndDate: null,
      viewMode: 'summary',
      filters: {
        store: '',
        type: '',
        status: '',
        search: ''
      },
      analyticsFilter: {
        type: 'all'
      },
      currentPage: 1,
      itemsPerPage: 25,
      selectedDailyLog: null,
      showDailyTaskModal: false,
      dataValidationErrors: [],
      viewModes: [
        { value: 'summary', label: 'Summary' },
        { value: 'detailed', label: 'Detailed' },
        { value: 'analytics', label: 'Analytics' }
      ],
      trendTooltip: {
        show: false,
        x: 0,
        y: 0,
        data: {}
      },
      sortConfig: {
        column: 'date',
        direction: 'desc'
      },
      summarySortConfig: {
        column: 'date',
        direction: 'desc'
      }
    }
  },
  computed: {
    ...mapState('config', ['isDataLoaded']),
    ...mapGetters('user', ['isAdmin']),
    ...mapState('admin', [
      'allStoreLogs',
      'detailedTaskData',
      'storeDetails',
      'userDetails',
      'isLoading'
    ]),
    ...mapGetters('admin', ['storeNameById', 'userNameById']),

    taskLogs() {
      return this.allStoreLogs || []
    },

    summaryStats() {
      const logs = this.taskLogs
      const totalLogs = logs.length
      const completedLogs = logs.filter((log) => log.completed).length
      const openLogs = totalLogs - completedLogs
      const completionRate =
        totalLogs > 0 ? Math.round((completedLogs / totalLogs) * 100) : 0

      return {
        totalLogs,
        completedLogs,
        openLogs,
        completionRate
      }
    },

    hasActiveFilters() {
      return !!(
        this.filters.store ||
        this.filters.type ||
        this.filters.status ||
        this.filters.search
      )
    },

    analyticsStats() {
      const logs = this.filteredTaskLogs
      const totalLogs = logs.length
      const completedLogs = logs.filter((log) => log.completed).length
      const averageCompletion =
        logs.length > 0
          ? logs.reduce(
              (sum, log) => sum + this.displayCompletionPercentage(log),
              0
            ) / logs.length
          : 0

      return {
        totalLogs,
        completedLogs,
        averageCompletion: Math.round(averageCompletion),
        completionRate:
          totalLogs > 0 ? Math.round((completedLogs / totalLogs) * 100) : 0
      }
    },

    loading() {
      return this.isLoading
    },

    hasData() {
      return (
        (this.viewMode === 'summary' && this.taskLogs.length > 0) ||
        (this.viewMode === 'detailed' &&
          this.filteredDetailedTasks.length > 0) ||
        (this.viewMode === 'analytics' && this.taskLogs.length > 0)
      )
    },

    dateRangeStats() {
      if (!this.startDate || !this.endDate) {
        return { totalDays: 0 }
      }

      const start = DateTime.fromJSDate(this.startDate)
      const end = DateTime.fromJSDate(this.endDate)
      const totalDays = Math.floor(end.diff(start, 'days').days) + 1

      return {
        totalDays,
        isValid: totalDays > 0 && totalDays <= 365 // Reasonable limits
      }
    },

    storeOptions() {
      // Get store names from storeDetails with validation
      const stores = Object.values(this.storeDetails || {})
        .map((store) => store.name)
        .filter((name) => name && typeof name === 'string') // Enhanced validation
        .sort()

      // Add validation error if no stores found
      if (
        stores.length === 0 &&
        Object.keys(this.storeDetails || {}).length > 0
      ) {
        this.addValidationError('Store names missing or invalid')
      }

      return stores
    },

    filteredDetailedTasks() {
      if (!this.detailedTaskData) return []

      // Add debug logging to see what's in detailedTaskData
      console.log(`Total detailed tasks: ${this.detailedTaskData.length}`)
      const dailyCount = this.detailedTaskData.filter(
        (t) => t.type === 'daily'
      ).length
      const weeklyCount = this.detailedTaskData.filter(
        (t) => t.type === 'weekly'
      ).length
      const monthlyCount = this.detailedTaskData.filter(
        (t) => t.type === 'monthly'
      ).length
      console.log(
        `Types - daily: ${dailyCount}, weekly: ${weeklyCount}, monthly: ${monthlyCount}`
      )

      return this.detailedTaskData.filter((task) => {
        // Apply store filter
        if (this.filters.store && task.storeName !== this.filters.store) {
          return false
        }

        // Apply type filter - ensure we handle daily logs properly
        if (this.filters.type) {
          if (this.filters.type !== task.type) {
            return false
          }
        }

        // Apply status filter
        if (this.filters.status === 'completed' && !task.completed) {
          return false
        }
        if (this.filters.status === 'open' && task.completed) {
          return false
        }

        // Apply search filter
        if (this.filters.search) {
          const searchTerm = this.filters.search.toLowerCase()
          const nameMatch =
            task.name && task.name.toLowerCase().includes(searchTerm)
          const storeMatch =
            task.storeName && task.storeName.toLowerCase().includes(searchTerm)
          const completedByMatch =
            task.completedBy &&
            task.completedBy.toLowerCase().includes(searchTerm)

          return nameMatch || storeMatch || completedByMatch
        }

        return true
      })
    },

    totalPages() {
      return Math.ceil(this.filteredDetailedTasks.length / this.itemsPerPage)
    },

    paginationStart() {
      return (this.currentPage - 1) * this.itemsPerPage
    },

    paginationEnd() {
      return Math.min(
        this.paginationStart + this.itemsPerPage,
        this.filteredDetailedTasks.length
      )
    },

    sortedDetailedTasks() {
      const tasks = [...this.filteredDetailedTasks]

      if (!this.sortConfig.column) return tasks

      return tasks.sort((a, b) => {
        let aVal = a[this.sortConfig.column]
        let bVal = b[this.sortConfig.column]

        // Handle special cases
        if (
          this.sortConfig.column === 'date' ||
          this.sortConfig.column === 'completedAt'
        ) {
          // Convert dates to timestamps for comparison
          aVal = aVal && aVal.seconds ? aVal.seconds : 0
          bVal = bVal && bVal.seconds ? bVal.seconds : 0
        } else if (this.sortConfig.column === 'completed') {
          // Convert boolean to number for sorting
          aVal = aVal ? 1 : 0
          bVal = bVal ? 1 : 0
        } else if (typeof aVal === 'string') {
          // Case-insensitive string comparison
          aVal = aVal.toLowerCase()
          bVal = (bVal || '').toLowerCase()
        }

        // Handle null/undefined values
        if (aVal === null || aVal === undefined) aVal = ''
        if (bVal === null || bVal === undefined) bVal = ''

        let comparison = 0
        if (aVal < bVal) comparison = -1
        if (aVal > bVal) comparison = 1

        return this.sortConfig.direction === 'desc' ? -comparison : comparison
      })
    },

    sortedTaskLogs() {
      const logs = [...this.taskLogs]

      if (!this.summarySortConfig.column) return logs

      return logs.sort((a, b) => {
        let aVal = a[this.summarySortConfig.column]
        let bVal = b[this.summarySortConfig.column]

        // Handle special cases
        if (this.summarySortConfig.column === 'date') {
          // Convert dates to timestamps for comparison
          aVal = aVal && aVal.seconds ? aVal.seconds : 0
          bVal = bVal && bVal.seconds ? bVal.seconds : 0
        } else if (this.summarySortConfig.column === 'completed') {
          // Convert boolean to number for sorting
          aVal = aVal ? 1 : 0
          bVal = bVal ? 1 : 0
        } else if (this.summarySortConfig.column === 'completionPercentage') {
          // Use completion percentage for sorting
          aVal = this.displayCompletionPercentage(a)
          bVal = this.displayCompletionPercentage(b)
        } else if (typeof aVal === 'string') {
          // Case-insensitive string comparison
          aVal = aVal.toLowerCase()
          bVal = (bVal || '').toLowerCase()
        }

        // Handle null/undefined values
        if (aVal === null || aVal === undefined) aVal = ''
        if (bVal === null || bVal === undefined) bVal = ''

        let comparison = 0
        if (aVal < bVal) comparison = -1
        if (aVal > bVal) comparison = 1

        return this.summarySortConfig.direction === 'desc'
          ? -comparison
          : comparison
      })
    },

    paginatedTasks() {
      return this.sortedDetailedTasks.slice(
        this.paginationStart,
        this.paginationEnd
      )
    },

    uniqueStores() {
      const stores = new Set()
      this.taskLogs.forEach((log) => {
        if (log.storeName) {
          stores.add(log.storeName)
        }
      })
      return Array.from(stores).sort()
    },

    uniqueDates() {
      const dates = new Set()
      this.taskLogs.forEach((log) => {
        if (log.date) {
          // Convert to date string for comparison
          const dateStr = this.getDateString(log.date)
          dates.add(dateStr)
        }
      })
      // Sort dates in descending order (newest first)
      return Array.from(dates).sort((a, b) => new Date(b) - new Date(a))
    },

    filteredTaskLogs() {
      if (this.analyticsFilter.type === 'all') {
        return this.taskLogs
      }
      return this.taskLogs.filter(
        (log) => (log.type || 'daily') === this.analyticsFilter.type
      )
    },

    storeCompletionRates() {
      const rates = {}

      this.uniqueStores.forEach((store) => {
        const storeLogs = this.filteredTaskLogs.filter(
          (log) => log.storeName === store
        )
        if (storeLogs.length === 0) {
          rates[store] = { percentage: 0, completed: 0, total: 0 }
          return
        }

        const completed = storeLogs.filter((log) => log.completed).length
        rates[store] = {
          percentage: Math.round((completed / storeLogs.length) * 100),
          completed,
          total: storeLogs.length
        }
      })

      return rates
    },

    typeCompletionRates() {
      const rates = {}
      const types = ['daily', 'weekly', 'monthly']

      types.forEach((type) => {
        const typeLogs = this.filteredTaskLogs.filter(
          (log) => (log.type || 'daily') === type
        )
        if (typeLogs.length === 0) {
          rates[type] = { percentage: 0, completed: 0, total: 0 }
          return
        }

        const completed = typeLogs.filter((log) => log.completed).length
        rates[type] = {
          percentage: Math.round((completed / typeLogs.length) * 100),
          completed,
          total: typeLogs.length
        }
      })

      return rates
    },

    completionTrend() {
      // Use the selected date range for the trend with improved calculation
      if (!this.startDate || !this.endDate) {
        return []
      }

      const trend = []
      const start = DateTime.fromJSDate(this.startDate).startOf('day')
      const end = DateTime.fromJSDate(this.endDate).startOf('day')

      // Calculate the number of days in the range (inclusive)
      const daysDiff = Math.floor(end.diff(start, 'days').days) + 1

      // Limit to reasonable number of data points for chart readability
      const maxDataPoints = 31
      const step =
        daysDiff > maxDataPoints ? Math.ceil(daysDiff / maxDataPoints) : 1

      for (let i = 0; i < daysDiff; i += step) {
        const date = start.plus({ days: i })
        const dateStr = date.toFormat('yyyy-MM-dd')
        const displayDate = date.toFormat('MM/dd')

        // Find logs for this date with improved matching
        const logsForDate = this.filteredTaskLogs.filter((log) => {
          const logDate = this.getDateString(log.date)
          return logDate === dateStr
        })

        if (logsForDate.length === 0) {
          trend.push({
            date: displayDate,
            percentage: 0,
            completed: 0,
            total: 0,
            dateStr
          })
          continue
        }

        // Calculate completion based on task counts when available, fallback to log status
        let totalTasks = 0
        let completedTasks = 0

        logsForDate.forEach((log) => {
          if (log.totalTasksCount && log.completedTasksCount !== undefined) {
            totalTasks += log.totalTasksCount
            completedTasks += log.completedTasksCount
          } else {
            totalTasks += 1
            completedTasks += log.completed ? 1 : 0
          }
        })

        const percentage =
          totalTasks > 0 ? Math.round((completedTasks / totalTasks) * 100) : 0

        trend.push({
          date: displayDate,
          percentage,
          completed: completedTasks,
          total: totalTasks,
          dateStr,
          logsCount: logsForDate.length
        })
      }

      return trend
    }
  },
  watch: {
    filters: {
      handler() {
        // Reset to first page when filters change
        this.currentPage = 1
      },
      deep: true
    }
  },
  created() {
    // Check if user is admin, if not redirect
    if (!this.isAdmin) {
      this.$router.push('/dashboard')
      return
    }

    // Initialize date range based on default preset
    this.initializeDateRange()

    // Don't automatically load data - user must click refresh
  },
  methods: {
    ...mapActions('admin', [
      'fetchAllStoreLogs',
      'fetchDetailedTaskData',
      'fetchStoreDetails',
      'fetchUserDetails'
    ]),

    async refreshData() {
      try {
        this.clearValidationErrors()

        // Ensure we have valid date range
        if (!this.startDate || !this.endDate) {
          this.initializeDateRange()
        }

        // Validate date range before proceeding
        if (!this.validateDateRange()) {
          return
        }

        // Store the date range being used for this refresh
        this.lastRefreshedStartDate = this.startDate
        this.lastRefreshedEndDate = this.endDate

        // First fetch store and user details
        await this.fetchStoreDetails()
        await this.fetchUserDetails()

        // Then fetch logs and task data using date range with timezone handling
        const dateRange = {
          startDate: this.startDate,
          endDate: this.endDate
        }

        await this.fetchAllStoreLogs(dateRange)
        await this.fetchDetailedTaskData(dateRange)

        // Validate data consistency
        this.validateDataConsistency()

        // Debug log to check what types of tasks we have
        if (this.detailedTaskData && this.detailedTaskData.length) {
          const typeCount = {}
          this.detailedTaskData.forEach((task) => {
            const type = task.type || 'daily'
            typeCount[type] = (typeCount[type] || 0) + 1
          })
          console.log('Task types in detailed data:', typeCount)

          // Log a sample of each type for debugging
          if (typeCount.daily) {
            const dailySample = this.detailedTaskData.find(
              (t) => t.type === 'daily'
            )
            console.log('Sample daily task:', dailySample)
          }
          if (typeCount.weekly) {
            const weeklySample = this.detailedTaskData.find(
              (t) => t.type === 'weekly'
            )
            console.log('Sample weekly task:', weeklySample)
          }
        }
      } catch (error) {
        console.error('Error refreshing data:', error)
        this.addValidationError('Failed to refresh data. Please try again.')
      }
    },

    validateDataConsistency() {
      // Check for data consistency between summary and detailed views
      const summaryTaskCount = this.taskLogs.reduce((total, log) => {
        return total + (log.totalTasksCount || 0)
      }, 0)

      const detailedTaskCount = this.detailedTaskData.length

      if (
        Math.abs(summaryTaskCount - detailedTaskCount) >
        summaryTaskCount * 0.1
      ) {
        this.addValidationError(
          'Data inconsistency detected between summary and detailed views'
        )
      }

      // Check for missing timestamps
      const logsWithoutDates = this.taskLogs.filter((log) => !log.date)
      if (logsWithoutDates.length > 0) {
        this.addValidationError(
          `${logsWithoutDates.length} logs missing date information`
        )
      }

      // Check for invalid completion percentages
      const invalidPercentages = this.taskLogs.filter((log) => {
        const percentage = this.displayCompletionPercentage(log)
        return percentage < 0 || percentage > 100
      })
      if (invalidPercentages.length > 0) {
        this.addValidationError(
          `${invalidPercentages.length} logs have invalid completion percentages`
        )
      }
    },

    initializeDateRange() {
      // Set default date range based on selected preset
      this.onPresetChange()
    },

    onPresetChange() {
      const today = DateTime.now()
      this.clearValidationErrors()

      switch (this.selectedPreset) {
        case 'today':
          this.startDate = today.toJSDate()
          this.endDate = today.toJSDate()
          break
        case 'yesterday':
          const yesterday = today.minus({ days: 1 })
          this.startDate = yesterday.toJSDate()
          this.endDate = yesterday.toJSDate()
          break
        case 'last7':
          this.startDate = today.minus({ days: 6 }).toJSDate()
          this.endDate = today.toJSDate()
          break
        case 'last14':
          this.startDate = today.minus({ days: 13 }).toJSDate()
          this.endDate = today.toJSDate()
          break
        case 'last30':
          this.startDate = today.minus({ days: 29 }).toJSDate()
          this.endDate = today.toJSDate()
          break
        case 'custom':
          // Don't change dates for custom - let user pick
          if (!this.startDate) {
            this.startDate = today.minus({ days: 13 }).toJSDate()
          }
          if (!this.endDate) {
            this.endDate = today.toJSDate()
          }
          break
      }

      // Validate date range
      this.validateDateRange()
    },

    onDateChange() {
      // When dates are manually changed, set preset to custom
      this.selectedPreset = 'custom'
      this.clearValidationErrors()
      this.validateDateRange()
    },

    validateDateRange() {
      this.clearValidationErrors()

      if (!this.startDate || !this.endDate) {
        this.addValidationError('Start and end dates are required')
        return false
      }

      const start = DateTime.fromJSDate(this.startDate)
      const end = DateTime.fromJSDate(this.endDate)

      if (start > end) {
        this.addValidationError('Start date must be before end date')
        return false
      }

      const daysDiff = end.diff(start, 'days').days
      if (daysDiff > 365) {
        this.addValidationError('Date range cannot exceed 365 days')
        return false
      }

      return true
    },

    addValidationError(message) {
      if (!this.dataValidationErrors.includes(message)) {
        this.dataValidationErrors.push(message)
      }
    },

    clearValidationErrors() {
      this.dataValidationErrors = []
    },

    getModernStatusClass(completed) {
      return completed
        ? 'inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800'
        : 'inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-800'
    },

    getProgressBarClass(percentage) {
      if (percentage >= 100) return 'bg-green-500'
      if (percentage >= 75) return 'bg-blue-500'
      if (percentage >= 50) return 'bg-yellow-500'
      if (percentage >= 25) return 'bg-orange-500'
      return 'bg-red-500'
    },

    clearFilters() {
      this.filters = {
        store: '',
        type: '',
        status: '',
        search: ''
      }
    },

    getCompletionRingColor(percentage) {
      if (percentage >= 100) return 'text-green-500'
      if (percentage >= 75) return 'text-blue-500'
      if (percentage >= 50) return 'text-amber-500'
      if (percentage >= 25) return 'text-orange-500'
      return 'text-red-500'
    },

    getStoreColorClass(store) {
      const colors = [
        'bg-blue-500',
        'bg-green-500',
        'bg-purple-500',
        'bg-pink-500',
        'bg-indigo-500',
        'bg-yellow-500'
      ]
      const index =
        Math.abs(store.split('').reduce((a, b) => a + b.charCodeAt(0), 0)) %
        colors.length
      return colors[index]
    },

    getTypeColorClass(type) {
      const colorMap = {
        daily: 'bg-blue-500',
        weekly: 'bg-green-500',
        monthly: 'bg-purple-500'
      }
      return colorMap[type] || 'bg-gray-500'
    },

    getTypeProgressClass(type, percentage) {
      const baseClass = this.getProgressBarClass(percentage)
      const typeColorMap = {
        daily: baseClass.replace('bg-', 'bg-blue-'),
        weekly: baseClass.replace('bg-', 'bg-green-'),
        monthly: baseClass.replace('bg-', 'bg-purple-')
      }
      return typeColorMap[type] || baseClass
    },

    getTrendBarClass(percentage) {
      if (percentage >= 90)
        return 'bg-gradient-to-t from-green-400 to-green-500'
      if (percentage >= 75) return 'bg-gradient-to-t from-blue-400 to-blue-500'
      if (percentage >= 50)
        return 'bg-gradient-to-t from-yellow-400 to-yellow-500'
      if (percentage >= 25)
        return 'bg-gradient-to-t from-orange-400 to-orange-500'
      return 'bg-gradient-to-t from-red-400 to-red-500'
    },

    filterByStore(store) {
      this.filters.store = store
      this.viewMode = 'detailed'
    },

    filterByType(type) {
      this.filters.type = type
      this.viewMode = 'detailed'
    },

    showTrendTooltip(data, event) {
      const rect = event.target.getBoundingClientRect()
      this.trendTooltip = {
        show: true,
        x: rect.left + rect.width / 2 - 50,
        y: rect.top - 80,
        data
      }
    },

    hideTrendTooltip() {
      this.trendTooltip.show = false
    },

    // Sorting functionality
    sortBy(column) {
      if (this.sortConfig.column === column) {
        // Toggle direction if same column
        this.sortConfig.direction =
          this.sortConfig.direction === 'asc' ? 'desc' : 'asc'
      } else {
        // Set new column with default direction
        this.sortConfig.column = column
        this.sortConfig.direction =
          column === 'date' || column === 'completedAt' ? 'desc' : 'asc'
      }
      // Reset to first page when sorting changes
      this.currentPage = 1
    },

    getSortIconClass(column, direction) {
      if (this.sortConfig.column !== column) {
        return 'text-gray-300'
      }
      return this.sortConfig.direction === direction
        ? 'text-gray-600'
        : 'text-gray-300'
    },

    getTypeTagClass(type) {
      const typeClasses = {
        daily: 'bg-blue-100 text-blue-800',
        weekly: 'bg-green-100 text-green-800',
        monthly: 'bg-purple-100 text-purple-800'
      }
      return typeClasses[type] || 'bg-gray-100 text-gray-800'
    },

    // Summary view sorting functionality
    sortSummaryBy(column) {
      if (this.summarySortConfig.column === column) {
        // Toggle direction if same column
        this.summarySortConfig.direction =
          this.summarySortConfig.direction === 'asc' ? 'desc' : 'asc'
      } else {
        // Set new column with default direction
        this.summarySortConfig.column = column
        this.summarySortConfig.direction = column === 'date' ? 'desc' : 'asc'
      }
    },

    getSummarySortIconClass(column, direction) {
      if (this.summarySortConfig.column !== column) {
        return 'text-gray-300'
      }
      return this.summarySortConfig.direction === direction
        ? 'text-gray-600'
        : 'text-gray-300'
    },

    // Debug method to inspect analytics data
    debugAnalyticsData() {
      console.log('=== ANALYTICS DEBUG REPORT ===')

      // 1. Check basic data availability
      console.log('1. Basic Data:')
      console.log('- taskLogs.length:', this.taskLogs.length)
      console.log('- filteredTaskLogs.length:', this.filteredTaskLogs.length)
      console.log('- analyticsFilter.type:', this.analyticsFilter.type)
      console.log('- startDate:', this.startDate)
      console.log('- endDate:', this.endDate)

      // 2. Sample task logs
      console.log('\n2. Sample Task Logs:')
      if (this.taskLogs.length > 0) {
        console.log(
          'First 3 task logs:',
          this.taskLogs.slice(0, 3).map((log) => ({
            id: log.id,
            storeName: log.storeName,
            type: log.type || 'daily',
            completed: log.completed,
            completedTasksCount: log.completedTasksCount,
            totalTasksCount: log.totalTasksCount,
            completionPercentage: log.completionPercentage,
            date: log.date
          }))
        )
      } else {
        console.log('No task logs available')
      }

      // 3. Check unique stores and dates
      console.log('\n3. Unique Data:')
      console.log('- uniqueStores:', this.uniqueStores)
      console.log('- uniqueDates:', this.uniqueDates)

      // 4. Check computed properties
      console.log('\n4. Computed Properties:')
      console.log('- storeCompletionRates:', this.storeCompletionRates)
      console.log('- typeCompletionRates:', this.typeCompletionRates)
      console.log('- completionTrend.length:', this.completionTrend.length)
      if (this.completionTrend.length > 0) {
        console.log(
          '- completionTrend sample:',
          this.completionTrend.slice(0, 3)
        )
      }

      // 5. Check date filtering
      console.log('\n5. Date Filtering:')
      if (this.taskLogs.length > 0) {
        const dateStrings = this.taskLogs.map((log) =>
          this.getDateString(log.date)
        )
        console.log('- Log date strings:', [...new Set(dateStrings)].sort())

        const startStr = this.startDate
          ? DateTime.fromJSDate(this.startDate).toFormat('yyyy-MM-dd')
          : 'none'
        const endStr = this.endDate
          ? DateTime.fromJSDate(this.endDate).toFormat('yyyy-MM-dd')
          : 'none'
        console.log('- Selected range:', startStr, 'to', endStr)

        // Check if any logs fall within the date range
        const logsInRange = this.taskLogs.filter((log) => {
          const logDateStr = this.getDateString(log.date)
          return logDateStr >= startStr && logDateStr <= endStr
        })
        console.log('- Logs in date range:', logsInRange.length)
      }

      console.log('=== END DEBUG REPORT ===')
    },

    formatDateRange() {
      if (!this.startDate || !this.endDate) {
        return 'No date range selected'
      }

      const start = DateTime.fromJSDate(this.startDate)
      const end = DateTime.fromJSDate(this.endDate)

      if (start.hasSame(end, 'day')) {
        return start.toFormat('MM/dd/yyyy')
      } else {
        return `${start.toFormat('MM/dd/yyyy')} - ${end.toFormat('MM/dd/yyyy')}`
      }
    },

    formatRefreshedDateRange() {
      if (!this.lastRefreshedStartDate || !this.lastRefreshedEndDate) {
        return 'No data loaded'
      }

      const start = DateTime.fromJSDate(this.lastRefreshedStartDate)
      const end = DateTime.fromJSDate(this.lastRefreshedEndDate)

      if (start.hasSame(end, 'day')) {
        return start.toFormat('MM/dd/yyyy')
      } else {
        return `${start.toFormat('MM/dd/yyyy')} - ${end.toFormat('MM/dd/yyyy')}`
      }
    },

    formatDate(timestamp) {
      if (!timestamp) return 'N/A'
      const date = timestamp.seconds
        ? DateTime.fromSeconds(timestamp.seconds)
        : DateTime.fromJSDate(timestamp)
      return date.toFormat('MM/dd/yyyy')
    },

    formatDateTime(timestamp) {
      if (!timestamp) return 'N/A'

      // Handle ISO string format
      if (typeof timestamp === 'string') {
        return DateTime.fromISO(timestamp).toFormat('MM/dd/yyyy hh:mm a')
      }

      // Handle Firestore timestamp
      const date = timestamp.seconds
        ? DateTime.fromSeconds(timestamp.seconds)
        : DateTime.fromJSDate(timestamp)
      return date.toFormat('MM/dd/yyyy hh:mm a')
    },

    getStatusClass(completed) {
      return completed
        ? 'text-green-600 font-medium'
        : 'text-yellow-600 font-medium'
    },

    displayCompletionPercentage(log) {
      // First try to use the pre-calculated percentage
      if (typeof log.completionPercentage === 'number') {
        return log.completionPercentage
      }

      // If that's not available, calculate it from the tasks array
      return this.calculateCompletion(log)
    },

    calculateCompletion(log) {
      if (!log.tasks || log.tasks.length === 0) return 0

      const completedTasks = log.tasks.filter((task) => task.completed).length
      return Math.round((completedTasks / log.tasks.length) * 100)
    },

    viewTaskDetails(log) {
      this.selectedLog = log
      this.$modal.show('task-details-modal')
    },

    viewAnalyticsDetails(date, store) {
      // Find the task logs for this specific date and store (using the same logic as getCompletionData)
      const targetLogs = this.filteredTaskLogs.filter(log => {
        const logDate = this.getDateString(log.date)
        return logDate === date && log.storeName === store
      })
      
      if (targetLogs.length > 0) {
        // If multiple logs exist for the same date/store, use the first one
        // This matches the behavior of the summary view
        this.selectedLog = targetLogs[0]
        this.$modal.show('task-details-modal')
      } else {
        console.warn(`No task log found for ${store} on ${date}`)
      }
    },

    exportCsv() {
      if (this.viewMode === 'summary' && !this.taskLogs.length) return
      if (this.viewMode === 'detailed' && !this.filteredDetailedTasks.length)
        return

      let csvContent = ''

      if (this.viewMode === 'summary') {
        // Summary view export
        const headers = [
          'Store',
          'Type',
          'Date Created',
          'Status',
          'Completion %',
          'Tasks Completed',
          'Total Tasks'
        ]

        const rows = this.taskLogs.map((log) => {
          return [
            this.escapeCsvValue(log.storeName),
            this.escapeCsvValue(log.type || 'daily'),
            this.escapeCsvValue(this.formatDate(log.date)),
            this.escapeCsvValue(log.completed ? 'Completed' : 'Open'),
            this.escapeCsvValue(this.displayCompletionPercentage(log)),
            this.escapeCsvValue(log.completedTasksCount || 0),
            this.escapeCsvValue(log.totalTasksCount || 0)
          ]
        })

        csvContent = [
          headers.join(','),
          ...rows.map((row) => row.join(','))
        ].join('\n')
      } else {
        // Detailed view export - use all filtered tasks, not just current page
        const headers = [
          'Task Name',
          'Store',
          'Type',
          'Date',
          'Status',
          'Completed By',
          'Completed At'
        ]

        // Use filteredDetailedTasks instead of paginatedTasks to include all tasks
        const rows = this.filteredDetailedTasks.map((task) => {
          return [
            this.escapeCsvValue(task.name),
            this.escapeCsvValue(task.storeName),
            this.escapeCsvValue(task.type || 'daily'),
            this.escapeCsvValue(this.formatDate(task.date)),
            this.escapeCsvValue(task.completed ? 'Completed' : 'Open'),
            this.escapeCsvValue(task.completedBy || ''),
            this.escapeCsvValue(
              task.completedAt ? this.formatDateTime(task.completedAt) : ''
            )
          ]
        })

        csvContent = [
          headers.join(','),
          ...rows.map((row) => row.join(','))
        ].join('\n')
      }

      // Download using the correct function signature
      downloadCsv({
        filename: `task-audit-${this.viewMode}-${DateTime.now().toFormat(
          'yyyy-MM-dd'
        )}`,
        data: csvContent
      })
    },

    escapeCsvValue(value) {
      if (value === null || value === undefined) return ''
      const stringValue = String(value)
      // If the value contains a comma, quote, or newline, wrap it in quotes and escape any quotes
      if (
        stringValue.includes(',') ||
        stringValue.includes('"') ||
        stringValue.includes('\n')
      ) {
        return `"${stringValue.replace(/"/g, '""')}"`
      }
      return stringValue
    },

    inspectTaskData() {
      console.log('All detailed task data:', this.detailedTaskData)

      // Count tasks by type
      const typeCount = {}
      this.detailedTaskData.forEach((task) => {
        const type = task.type || 'daily'
        typeCount[type] = (typeCount[type] || 0) + 1
      })
      console.log('Task types in detailed data:', typeCount)

      // Log a sample of each type for debugging
      for (const type of ['daily', 'weekly', 'monthly']) {
        if (typeCount[type]) {
          const sample = this.detailedTaskData.find((t) => t.type === type)
          console.log(`Sample ${type} task:`, sample)
        }
      }

      // Check task logs for completion data
      console.log('Task logs with completion data:')
      this.taskLogs.forEach((log) => {
        console.log(`Log ${log.id} (${log.type || 'daily'}):`, {
          storeName: log.storeName,
          completed: log.completed,
          completedTasksCount: log.completedTasksCount,
          totalTasksCount: log.totalTasksCount,
          completionPercentage: log.completionPercentage,
          hasTasksArray: Array.isArray(log.tasks) && log.tasks.length > 0
        })
      })
    },

    getCompletionData(dateStr, storeName) {
      const logsForDateAndStore = this.filteredTaskLogs.filter((log) => {
        const logDate = this.getDateString(log.date)
        return logDate === dateStr && log.storeName === storeName
      })

      if (logsForDateAndStore.length === 0) {
        return null
      }

      // Check if we have completedTasksCount and totalTasksCount properties
      const hasTaskCounts = logsForDateAndStore.some(
        (log) =>
          typeof log.completedTasksCount === 'number' &&
          typeof log.totalTasksCount === 'number' &&
          log.totalTasksCount > 0
      )

      if (hasTaskCounts) {
        // Use pre-calculated task counts if available
        let totalCompleted = 0
        let totalTasks = 0

        logsForDateAndStore.forEach((log) => {
          totalCompleted += log.completedTasksCount || 0
          totalTasks += log.totalTasksCount || 0
        })

        const percentage =
          totalTasks > 0 ? Math.round((totalCompleted / totalTasks) * 100) : 0
        return {
          completed: percentage === 100,
          percentage: percentage
        }
      } else {
        // Fall back to log completion status
        const completed = logsForDateAndStore.filter(
          (log) => log.completed
        ).length
        return {
          completed: completed === logsForDateAndStore.length,
          percentage: Math.round((completed / logsForDateAndStore.length) * 100)
        }
      }
    },

    getDateString(timestamp) {
      if (!timestamp) return ''
      const date = timestamp.seconds
        ? DateTime.fromSeconds(timestamp.seconds)
        : DateTime.fromJSDate(timestamp)
      return date.toFormat('yyyy-MM-dd')
    },

    capitalize(str) {
      return str.charAt(0).toUpperCase() + str.slice(1)
    },

    formatDateString(dateStr) {
      if (!dateStr) return 'N/A'
      try {
        // Parse the ISO date string and format it
        return DateTime.fromISO(dateStr).toFormat('MM/dd/yyyy')
      } catch (e) {
        console.error('Error formatting date string:', dateStr, e)
        return 'Invalid Date'
      }
    }
  }
}
</script>
